import { useEffect, useRef } from 'react';

/**
 * Polls while `enabled`: one request at a time, then waits `intervalMs` before
 * the next. Overlaps are never queued; unmount/disable ignores in-flight results.
 */
export const usePolling = (
  fn: (signal: { cancelled: boolean }) => Promise<void>,
  intervalMs: number,
  enabled: boolean,
) => {
  const fnRef = useRef(fn);

  fnRef.current = fn;

  useEffect(() => {
    if (!enabled) return undefined;

    const signal = { cancelled: false };
    let inFlight = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const schedule = (delayMs: number) => {
      timeoutId = setTimeout(() => {
        void tick();
      }, delayMs);
    };

    const tick = async () => {
      if (signal.cancelled || inFlight) return;

      inFlight = true;

      try {
        await fnRef.current(signal);
      } finally {
        inFlight = false;
      }

      if (!signal.cancelled) {
        schedule(intervalMs);
      }
    };

    schedule(0);

    return () => {
      signal.cancelled = true;

      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    };
  }, [enabled, intervalMs]);
};
